import type { BrokerCapabilities } from "@/services/brokerIntegrationApi"
import { accountSettingsTabPath } from "@/constants/accountSettingsSidebar"
import {
  isCustomCsvBrokerName,
  resolveBrokerDisplayName,
} from "@/utils/customCsvAccountDisplay"
import {
  collapseBrokersForConnectWizard,
} from "@/utils/tradingViewConnectBrand"
import type { ConnectBrokerImportMethodId } from "@/views/Dashboard/Profile/connectBroker/connectBrokerImportMethods"

/** Legacy full-page path — redirects onto Broker Management and opens the dialog. */
export const CONNECT_BROKER_WIZARD_PATH = "/profile/broker-api/connect"

/** Query flag that opens the connect-broker dialog on Broker Management. */
export const CONNECT_BROKER_WIZARD_OPEN_QUERY = "connect"

/** Slugs surfaced first in the broker picker grid (remaining brokers follow alphabetically). */
export const POPULAR_BROKER_SLUGS = [
  "binance",
  "ibkr",
  "liquidbrokers",
  "ctrader",
  "oanda",
  "tradingview",
  "hyperliquid",
  "mt5",
] as const

export type ConnectBrokerWizardUrlOptions = {
  brokerName: string
  importMethod?: ConnectBrokerImportMethodId
}

export type ConnectBrokerWizardOpenSeed = {
  brokerName?: string | null
  importMethod?: ConnectBrokerImportMethodId | null
}

export function normalizeConnectBrokerSlug(brokerName: string): string {
  return brokerName.trim().toLowerCase()
}

/** Broker Management URL that opens the connect dialog (optional deep-link seed). */
export function buildConnectBrokerManagementOpenUrl(
  options?: ConnectBrokerWizardOpenSeed
): string {
  const params = new URLSearchParams()
  params.set(CONNECT_BROKER_WIZARD_OPEN_QUERY, "1")
  const slug = options?.brokerName
    ? normalizeConnectBrokerSlug(options.brokerName)
    : ""
  if (slug) params.set("broker", slug)
  if (options?.importMethod) params.set("method", options.importMethod)
  return `${accountSettingsTabPath("broker-api")}?${params.toString()}`
}

/**
 * Deep link into the connect wizard (e.g. bookmarks / Import trades).
 * Still uses `/connect`; that route redirects to Broker Management with `connect=1`.
 */
export function buildConnectBrokerWizardUrl(
  options: ConnectBrokerWizardUrlOptions
): string {
  const params = new URLSearchParams()
  const slug = normalizeConnectBrokerSlug(options.brokerName)
  if (slug) params.set("broker", slug)
  if (options.importMethod) params.set("method", options.importMethod)
  const qs = params.toString()
  return qs ? `${CONNECT_BROKER_WIZARD_PATH}?${qs}` : CONNECT_BROKER_WIZARD_PATH
}

/** Parse `?connect=1&broker=&method=` from Broker Management search. */
export function parseConnectBrokerWizardOpenSearch(search: string): {
  shouldOpen: boolean
  brokerName: string | null
  importMethod: ConnectBrokerImportMethodId | null
} {
  const sp = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
  const shouldOpen = sp.get(CONNECT_BROKER_WIZARD_OPEN_QUERY) === "1"
  const brokerRaw = (sp.get("broker") || sp.get("broker_name") || "").trim()
  const methodRaw = (sp.get("method") || sp.get("import") || "").trim()
  const importMethod =
    methodRaw === "auto_sync" || methodRaw === "file_upload" || methodRaw === "manual"
      ? methodRaw
      : null
  return {
    shouldOpen,
    brokerName: brokerRaw ? normalizeConnectBrokerSlug(brokerRaw) : null,
    importMethod,
  }
}

/** Strip connect-dialog open params; preserve other management query keys. */
export function stripConnectBrokerWizardOpenParams(search: string): string {
  const sp = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
  sp.delete(CONNECT_BROKER_WIZARD_OPEN_QUERY)
  sp.delete("broker")
  sp.delete("broker_name")
  sp.delete("method")
  sp.delete("import")
  const qs = sp.toString()
  return qs ? `?${qs}` : ""
}

export function sortBrokersForConnectWizard(
  brokers: BrokerCapabilities[]
): BrokerCapabilities[] {
  const listedBrokers = brokers.filter(
    (broker) => !isCustomCsvBrokerName(broker.broker_name)
  )
  const otherBroker = brokers.find((broker) =>
    isCustomCsvBrokerName(broker.broker_name)
  )

  const popularRank = new Map(
    POPULAR_BROKER_SLUGS.map((slug, index) => [slug, index])
  )

  const sortedListed = [...listedBrokers].sort((a, b) => {
    const aSlug = normalizeConnectBrokerSlug(a.broker_name)
    const bSlug = normalizeConnectBrokerSlug(b.broker_name)
    const aRank = popularRank.get(aSlug as (typeof POPULAR_BROKER_SLUGS)[number])
    const bRank = popularRank.get(bSlug as (typeof POPULAR_BROKER_SLUGS)[number])

    if (aRank != null && bRank != null) return aRank - bRank
    if (aRank != null) return -1
    if (bRank != null) return 1

    return resolveBrokerDisplayName(a).localeCompare(
      resolveBrokerDisplayName(b),
      undefined,
      { sensitivity: "base" }
    )
  })

  return otherBroker ? [...sortedListed, otherBroker] : sortedListed
}

/**
 * Grid list for the choose-broker step: collapses TradingView Backtest + Paper into one card,
 * then sorts with custom CSV last.
 */
export function resolveConnectWizardGridBrokers(
  brokers: BrokerCapabilities[]
): BrokerCapabilities[] {
  const collapsed = collapseBrokersForConnectWizard(brokers)
  const sorted = sortBrokersForConnectWizard(collapsed)
  const listedBrokers = sorted.filter(
    (broker) => !isCustomCsvBrokerName(broker.broker_name)
  )
  const otherBroker = sorted.find((broker) =>
    isCustomCsvBrokerName(broker.broker_name)
  )

  return otherBroker ? [...listedBrokers, otherBroker] : listedBrokers
}
